<template>
  <div
    v-loading="fetchInProgress"
    class="program-results"
    style="min-height: 200px;"
  >
    <div class="checkbox">
      <label>
        <input v-model="showApplicationsWithAnswersOnly" type="checkbox" />
        <span>Show only submitted</span>
      </label>
    </div>

    <p v-if="!fetchInProgress && !applications.length" class="text-muted">
      There are no submitted applications yet.
    </p>

    <div v-if="!fetchInProgress && applications.length">
      <el-table
        :data="extendedApplications"
        :default-sort="{ prop: 'ts', order: 'descending' }"
        :row-class-name="
          ({ row }) => (row.state === 'draft' ? 'text-muted' : '')
        "
        style="width: 100%;"
      >
        <el-table-column type="expand">
          <template slot-scope="{ row }">
            <div
              v-for="rating in row.allDefinedRatings"
              :key="rating.user.id"
              class="user-rating row"
            >
              <div class="col-sm-12 col-md-6 col-lg-4">
                <StartupRate :value="rating.overall" muted read-only />
              </div>
              <div class="col-sm-12 col-md-6 col-lg-4">
                {{ rating.user.email }}
              </div>
              <div class="col-sm-12 col-md-12 col-lg-4">
                <p>{{ rating.note }}</p>
              </div>
            </div>

            <p v-if="!row.allDefinedRatings.length" class="text-muted">
              This application has not been rated yet.
            </p>
          </template>
        </el-table-column>
        <el-table-column label="Name" prop="startup_name" sortable>
          <template slot-scope="{ row }">
            <a v-if="row.submitted_at" href @click.prevent="showResults(row)">
              {{ row.startup_name }}
            </a>
            <span v-if="!row.submitted_at">{{ row.startup_name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Registered" prop="ts" sortable>
          <template slot-scope="{ row }">
            {{ row.ts | date }}
          </template>
        </el-table-column>
        <el-table-column label="Submitted at" prop="submitted_at" sortable>
          <template slot-scope="{ row }">
            <span v-if="row.submitted_at">{{ row.submitted_at | date }}</span>
            <span v-if="!row.submitted_at">pending</span>
          </template>
        </el-table-column>
        <el-table-column label="E-mail" prop="email">
          <template slot-scope="{ row }">
            <CopyClick :text="row.email" class="text-ellipsis">
              {{ row.email }}
            </CopyClick>
          </template>
        </el-table-column>
        <el-table-column
          v-if="program.type === 'BOTH' || program.type === 'PROFILE'"
          label="Profile progress"
          prop="startup_profile_progress"
          sortable
        >
          <template slot-scope="{ row }">
            <span
              :class="{
                'text-success':
                  row.startup_profile_progress >= PROFILE_PROGRESS_THRESHOLD,
                'text-danger':
                  row.startup_profile_progress < PROFILE_PROGRESS_THRESHOLD,
              }"
              >{{ row.startup_profile_progress | number }}%</span
            >
          </template>
        </el-table-column>
        <el-table-column label="Accepted" prop="accepted" sortable>
          <template slot-scope="{ row }">
            <el-tag
              v-if="row.accepted"
              :type="getAcceptedTagType(row.accepted)"
              size="mini"
              >{{ row.accepted | capitalize }}</el-tag
            >
          </template>
        </el-table-column>
        <el-table-column label="Rating" prop="ratings.overall_avg" sortable>
          <template slot-scope="{ row }">
            {{ getApplicationRating(row) }}
          </template>
        </el-table-column>
      </el-table>

      <hr class="margin-top-30" />
      <p class="text-muted">
        Total: {{ totalCount }}
        <span v-if="showApplicationsWithAnswersOnly" class="text-muted">
          ({{ draftsCount }} drafts hidden)</span
        >
      </p>
      <button
        class="btn btn-success btn-transparent pull-right"
        @click.prevent="downloadExcelResultsSummary()"
      >
        <i class="ion ion-android-download"></i>
        Download results summary
      </button>
      <button
        class="btn btn-success btn-transparent pull-right"
        @click.prevent="downloadExcelCompaniesSummary()"
      >
        <i class="ion ion-android-download"></i>
        Download companies summary
      </button>
    </div>

    <ProgramApplicationModal ref="applicationModal" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import http from '@/services/http'
import ProgramApplicationModal from '@/modules/dashboard-organizer/ProgramApplicationModal'
import ProgramOrganizationIdMixin from '@/modules/dashboard-organizer/ProgramOrganizationIdMixin'
import CopyClick from '@/components/CopyClick'
import StartupRate from 'components/StartupRate.vue'
import { PROFILE_PROGRESS_THRESHOLD } from '@/services/startup-report'

export default {
  components: { CopyClick, ProgramApplicationModal, StartupRate },
  mixins: [ProgramOrganizationIdMixin],
  data: () => ({
    application: {},
    showApplicationsWithAnswersOnly: true,
    PROFILE_PROGRESS_THRESHOLD,
  }),
  computed: {
    modalTitle() {
      return `Answers from ${this.application.startup_name}`
    },
    excelResultsSummaryPath() {
      return `/organizations/${this.realOrganizationId}/programs/${this.programId}/applications-results.xlsx`
    },
    excelCompaniesSummaryPath() {
      return `/organizations/${this.realOrganizationId}/programs/${this.programId}/companies.xlsx`
    },
    ...mapGetters({
      totalCount: 'organizationProgramApplications/totalCount',
      draftsCount: 'organizationProgramApplications/draftsCount',
    }),
    ...mapState({
      fetchInProgress: (state) =>
        state.organizationProgramApplications.fetchInProgress,
      user: (state) => state.user.data,
    }),
    applications() {
      const store = this.$store
      if (this.showApplicationsWithAnswersOnly) {
        return store.getters['organizationProgramApplications/answered']
      } else {
        return store.state.organizationProgramApplications.applications
      }
    },
    extendedApplications() {
      return this.applications.map((a) => {
        const allDefinedRatings = this.getAllDefinedRatings(a.ratings.all)
        return { ...a, allDefinedRatings }
      })
    },
  },
  beforeDestroy() {
    this.$store.commit('organizationProgramApplications/CLEAN')
  },
  methods: {
    async showResults(application) {
      this.application = application

      const organizationId = this.realOrganizationId
      const programId = this.programId
      const applicationId = application.id
      this.$refs.applicationModal.open({
        organizationId,
        programId,
        applicationId,
      })
    },

    getApplicationRating(application) {
      if (application.ratings.overall_avg) {
        return application.ratings.overall_avg.toFixed(1)
      } else {
        return '-'
      }
    },

    async downloadExcelResultsSummary() {
      try {
        await http.downloadFile(
          this.excelResultsSummaryPath,
          'applications-results-summary.xlsx',
        )
      } catch (err) {
        this.$message.error({
          dangerouslyUseHTMLString: true,
          message: 'Something went wrong - please contact customer service',
        })
      }
    },

    async downloadExcelCompaniesSummary() {
      http.downloadFile(
        this.excelCompaniesSummaryPath,
        'companies-results-summary.xlsx',
      )
    },

    getAllDefinedRatings(ratings = []) {
      return ratings.filter((r) => r.overall)
    },

    onProgramFetchDidStart() {
      this.$store.dispatch('organizationProgramApplications/fetch', {
        organizationId: this.realOrganizationId,
        programId: this.programId,
      })
    },

    getAcceptedTagType(value) {
      switch (value) {
        case 'YES':
          return 'success'
        case 'NO':
          return 'danger'
        case 'MAYBE':
          return 'warning'
      }
    },
  },
}
</script>

<!-- fix for Safari not truncating the email column in the results table -->
<style lang="scss">
.el-table_1_column_5 {
  .cell {
    max-width: 160px;
  }
}

.program-results {
  .el-table .cell {
    overflow: hidden;
    word-break: unset;
  }

  .el-table__expanded-cell {
    h6 {
      margin-top: 0;
      font-weight: bold;
      color: #909399;
    }
  }
}
</style>

<style lang="scss" scoped>
.program-results {
  font-size: 14px;

  tr.text-muted {
    opacity: 0.4;
  }
}

.btn.pull-right {
  margin-left: 5px;
}
</style>
